/**
 * size-chart-regions.js — v1
 * ------------------------------------------------------------------
 * Size chart popup: inches/cm toggle + US / UK / EU / AU region switch.
 *
 * This is the original popup logic (formerly an inline script in
 * sections/main-product.liquid) with AU support added. It runs as a
 * deferred script, i.e. BEFORE the old inline DOMContentLoaded handler,
 * and marks each chart as initialised so the old handler skips it.
 *
 * AU behaviour, per chart table:
 *   1. The chart already has an AU column  -> used as is (a real AU column
 *      always wins, so values can be added by hand to any chart later).
 *   2. No AU column, but it has a UK (or US/UK) column:
 *        clothing  -> AU = UK  (standard women's and men's sizing)
 *        footwear  -> AU taken from the footwear conversion table below,
 *                     looked up by the label size in the first column
 *      An "AU" column is added right after the UK column and a small
 *      footnote is shown when AU is selected.
 *   3. Anything that can't be converted safely -> no AU column and the
 *      AU option is removed from that chart's dropdown (never blanks).
 *
 * Visitors from Australia open on AU automatically (if the chart has it).
 * Image-only charts (no <table>) are left exactly as they were.
 */
(function () {
  'use strict';

  var HAS_US = /(^|[^a-z])(US)([^a-z]|$)/i;
  var HAS_UK = /(^|[^a-z])(UK)([^a-z]|$)/i;
  var HAS_EU = /(^|[^a-z])(EU)([^a-z]|$)/i;
  var HAS_AU = /(^|[^a-z])(AUS?)([^a-z]|$)/i;
  var IS_SIZE = /(^|\s)size(\s|$)/i;
  var FOOTWEAR_HINT = /\b(shoes?|footwear|boots?|sneakers?|sandals?|heels?|loafers?|slippers?)\b|label\s*size/i;

  /* Footwear: label size -> AU size (from the store's own footwear chart) */
  var AU_SHOE = {
    '25': '-1',  '26': '-0.5', '27': '0',    '28': '0.5',  '29': '1',
    '30': '1.5', '31': '2',    '32': '2.5',  '33': '3',    '34': '3.5',
    '35': '4',   '36': '4.5',  '37': '5-5.5','38': '6',    '39': '6.5',
    '40': '7',   '41': '7.5',  '42': '8-8.5','43': '9',    '44': '9.5',
    '45': '10'
  };

  function ensureStyle() {
    if (document.getElementById('size-chart-au-style')) return;
    var s = document.createElement('style');
    s.id = 'size-chart-au-style';
    s.textContent =
      '.size-chart-au-note{margin:10px 0 0;text-align:center;font-size:12px;color:rgba(0,0,0,.6);}';
    document.head.appendChild(s);
  }

  /* Convert any numbers found inside a text string + swap unit words */
  function convertText(txt, fromUnit, toUnit) {
    var factor = fromUnit === 'in' && toUnit === 'cm' ? 2.54
               : fromUnit === 'cm' && toUnit === 'in' ? 1 / 2.54 : 1;
    var out = txt.replace(/(\d+(\.\d+)?)/g, function (m) {
      var n = parseFloat(m.replace(',', '.'));
      return isNaN(n) ? m : (n * factor).toFixed(1).replace(/\.0$/, '');
    });
    if (fromUnit === 'in' && toUnit === 'cm') {
      out = out
        .replace(/\b(inches|inch)\b/gi, 'centimeters')
        .replace(/\bin\.\b/gi, 'cm');
    } else if (fromUnit === 'cm' && toUnit === 'in') {
      out = out
        .replace(/\b(centimeters|centimetres|centimeter|centimetre|cm)\b/gi, 'inches');
    }
    return out;
  }

  /* Returns 'existing' | 'uk' | 'shoe' when the table ends up with an AU
     column, or null when AU can't be provided for this chart. */
  function addAuColumn(table, wrapper) {
    var headRow = table.querySelector('thead tr') || table.querySelector('tr');
    if (!headRow) return null;
    var headCells = Array.prototype.slice.call(headRow.children);
    if (!headCells.length) return null;

    for (var i = 0; i < headCells.length; i++) {
      if (HAS_AU.test(headCells[i].textContent || '')) return 'existing';
    }

    var src = -1;
    for (var j = 0; j < headCells.length; j++) {
      if (HAS_UK.test(headCells[j].textContent || '')) { src = j; break; }
    }
    if (src < 0) return null;

    var titleEl = wrapper.querySelector('.size-chart-title');
    var context = ((titleEl && titleEl.textContent) || '') + ' ' + (headCells[0].textContent || '');
    var isFootwear = FOOTWEAR_HINT.test(context);

    var bodyRows = Array.prototype.slice.call(table.querySelectorAll('tbody tr'));
    if (!bodyRows.length) {
      bodyRows = Array.prototype.slice.call(table.querySelectorAll('tr'))
        .filter(function (r) { return r !== headRow; });
    }
    if (!bodyRows.length) return null;

    /* Work out every AU value first — only touch the table if ALL rows
       can be converted, so a chart is never left half-filled. */
    var values = [];
    for (var r = 0; r < bodyRows.length; r++) {
      var cells = bodyRows[r].children;
      if (!cells[src]) return null;
      var value;
      if (isFootwear) {
        var label = (cells[0].textContent || '').trim();
        if (!Object.prototype.hasOwnProperty.call(AU_SHOE, label)) return null;
        value = AU_SHOE[label];
      } else {
        value = (cells[src].textContent || '').trim();
        if (!value) return null;
      }
      values.push(value);
    }

    var thAu = document.createElement('th');
    thAu.textContent = 'AU';
    thAu.className = headCells[src].className;
    if (headCells[src].getAttribute('style')) {
      thAu.setAttribute('style', headCells[src].getAttribute('style'));
    }
    headRow.insertBefore(thAu, headCells[src].nextSibling);

    bodyRows.forEach(function (row, idx) {
      var srcCell = row.children[src];
      var td = document.createElement('td');
      td.textContent = values[idx];
      td.className = srcCell.className;
      if (srcCell.getAttribute('style')) td.setAttribute('style', srcCell.getAttribute('style'));
      row.insertBefore(td, srcCell.nextSibling);
    });

    return isFootwear ? 'shoe' : 'uk';
  }

  function initWrapper(wrapper) {
    if (wrapper.dataset.sizeInitialized === 'true') return;
    wrapper.dataset.sizeInitialized = 'true';

    var table = wrapper.querySelector('table');
    if (!table) return;
    var toggleButtons = wrapper.querySelectorAll('.size-toggle-btn');
    if (!toggleButtons.length) return;
    var regionSelect = wrapper.querySelector('.size-region-select');

    /* ── AU column + dropdown option ── */
    var auMode = addAuColumn(table, wrapper);
    if (regionSelect) {
      var auOption = regionSelect.querySelector('option[value="AU"]');
      if (auMode && !auOption) {
        auOption = document.createElement('option');
        auOption.value = 'AU';
        auOption.textContent = 'AU';
        regionSelect.appendChild(auOption);
      } else if (!auMode && auOption) {
        auOption.parentNode.removeChild(auOption);
      }
    }

    var auNote = null;
    if (auMode === 'uk' || auMode === 'shoe') {
      ensureStyle();
      auNote = document.createElement('p');
      auNote.className = 'size-chart-au-note size-chart-hidden';
      auNote.textContent = auMode === 'shoe'
        ? 'AU sizes converted from our footwear size guide.'
        : 'AU sizes follow UK sizing.';
      var anchor = table.closest('.size-chart-table-wrapper') || table;
      anchor.parentNode.insertBefore(auNote, anchor.nextSibling);
    }

    /* ── Detect default unit from any heading containing "Measurements in …" ── */
    var defaultUnit = 'in';
    var measurementLabel = null;
    var headings = wrapper.querySelectorAll('p, h2, h3, h4, strong');
    for (var i = 0; i < headings.length; i++) {
      if (/measurements?\s+in\s+(cm|centimeter|centimetre|inch)/i.test(headings[i].textContent)) {
        measurementLabel = headings[i];
        break;
      }
    }
    if (measurementLabel) {
      var lw = measurementLabel.textContent.toLowerCase();
      if (lw.indexOf('cm') !== -1 || lw.indexOf('centimeter') !== -1 || lw.indexOf('centimetre') !== -1) {
        defaultUnit = 'cm';
      }
    }

    /* ── Extra text elements (p, li) that contain numbers + cm/inch ── */
    var extraTextEls = [];
    wrapper.querySelectorAll('p, li, span').forEach(function (el) {
      if (el === measurementLabel || el === auNote) return;
      if (el.querySelector('table, p, li')) return; /* skip containers */
      var t = el.textContent || '';
      if (!/\d/.test(t)) return;
      if (!/(cm|centimeter|centimetre|inch|inches)/i.test(t)) return;
      extraTextEls.push(el);
    });

    /* ── Headers / region columns / convert columns ── */
    var headerCells = Array.prototype.slice.call(table.querySelectorAll('thead th'));
    if (!headerCells.length) {
      var firstRow = table.querySelector('tr');
      if (firstRow) headerCells = Array.prototype.slice.call(firstRow.querySelectorAll('th, td'));
    }
    if (!headerCells.length) return;

    var convertIndexes = [];
    var regionColumns = { US: [], UK: [], EU: [], AU: [] };
    headerCells.forEach(function (th, idx) {
      var t = th.textContent.trim();
      var hasUS = HAS_US.test(t);
      var hasUK = HAS_UK.test(t);
      var hasEU = HAS_EU.test(t);
      var hasAU = HAS_AU.test(t);
      var isSz  = IS_SIZE.test(t);
      if (hasUS) regionColumns.US.push(idx);
      if (hasUK) regionColumns.UK.push(idx);
      if (hasEU) regionColumns.EU.push(idx);
      if (hasAU) regionColumns.AU.push(idx);
      if (!hasUS && !hasUK && !hasEU && !hasAU && !isSz && idx !== 0) convertIndexes.push(idx);
    });

    var rows = Array.prototype.slice.call(table.querySelectorAll('tbody tr'));
    if (!rows.length) rows = Array.prototype.slice.call(table.querySelectorAll('tr')).slice(1);

    /* ── Cache in/cm versions of each convertible cell ── */
    rows.forEach(function (row) {
      var cells = row.querySelectorAll('td');
      convertIndexes.forEach(function (ci) {
        var c = cells[ci];
        if (!c || !/\d/.test(c.textContent)) return;
        var orig = c.textContent.trim();
        if (defaultUnit === 'in') {
          c.dataset.in = orig;
          c.dataset.cm = convertText(orig, 'in', 'cm');
        } else {
          c.dataset.cm = orig;
          c.dataset.in = convertText(orig, 'cm', 'in');
        }
      });
    });

    /* ── Cache in/cm versions of the "Measurements in …" heading ── */
    if (measurementLabel) {
      var labelOrig = measurementLabel.textContent.trim();
      if (defaultUnit === 'cm') {
        measurementLabel.dataset.cm = labelOrig;
        measurementLabel.dataset.in = labelOrig
          .replace(/\b(centimeters|centimetres|centimeter|centimetre|cm)\b/gi, 'Inches');
      } else {
        measurementLabel.dataset.in = labelOrig;
        measurementLabel.dataset.cm = labelOrig
          .replace(/\b(inches|inch)\b/gi, 'Centimeters')
          .replace(/\bin\.\b/gi, 'cm');
      }
    }

    /* ── Cache in/cm versions of every extra text element ── */
    extraTextEls.forEach(function (el) {
      var orig = el.textContent.trim();
      if (!orig) return;
      var elUnit = /(cm|centimeter|centimetre)/i.test(orig) ? 'cm' : 'in';
      if (elUnit === 'in') {
        el.dataset.in = orig;
        el.dataset.cm = convertText(orig, 'in', 'cm');
      } else {
        el.dataset.cm = orig;
        el.dataset.in = convertText(orig, 'cm', 'in');
      }
    });

    /* ── Apply selected unit to everything ── */
    function applyUnit(unit) {
      rows.forEach(function (row) {
        var cells = row.querySelectorAll('td');
        convertIndexes.forEach(function (ci) {
          var c = cells[ci];
          if (c && c.dataset[unit]) c.textContent = c.dataset[unit];
        });
      });
      if (measurementLabel && measurementLabel.dataset[unit]) {
        measurementLabel.textContent = measurementLabel.dataset[unit];
      }
      extraTextEls.forEach(function (el) {
        if (el.dataset[unit]) el.textContent = el.dataset[unit];
      });
      toggleButtons.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.dataset.unit === unit);
      });
    }

    /* ── Apply selected region (show/hide US/UK/EU/AU columns) ── */
    function applyRegion(code) {
      var all = regionColumns.US.concat(regionColumns.UK, regionColumns.EU, regionColumns.AU);
      headerCells.forEach(function (th, i) {
        if (all.indexOf(i) !== -1) th.classList.add('size-chart-hidden');
      });
      rows.forEach(function (row) {
        var cells = row.querySelectorAll('td');
        all.forEach(function (i) { if (cells[i]) cells[i].classList.add('size-chart-hidden'); });
      });
      if (auNote) auNote.classList.toggle('size-chart-hidden', code !== 'AU');
      if (!code || !regionColumns[code] || !regionColumns[code].length) return;
      regionColumns[code].forEach(function (ci) {
        if (headerCells[ci]) headerCells[ci].classList.remove('size-chart-hidden');
        rows.forEach(function (row) {
          var c = row.querySelectorAll('td')[ci];
          if (c) c.classList.remove('size-chart-hidden');
        });
      });
    }

    toggleButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { applyUnit(btn.dataset.unit); });
    });
    if (regionSelect) {
      regionSelect.addEventListener('change', function () { applyRegion(regionSelect.value); });
    }

    /* ── Auto-pick region from the visitor's country ── */
    var vs = document.querySelector('variant-selects');
    var country = (
      (window.Shopify && window.Shopify.country) ||
      (vs && vs.getAttribute('data-country')) ||
      'EU'
    ).toString().toUpperCase();
    var region = 'EU';
    if (['US', 'CA'].indexOf(country) !== -1) region = 'US';
    else if (['GB', 'UK'].indexOf(country) !== -1) region = 'UK';
    else if (country === 'AU') region = 'AU';

    if (regionSelect && regionColumns[region] && regionColumns[region].length) {
      regionSelect.value = region;
    }

    applyUnit(defaultUnit);
    if (regionSelect && regionSelect.value) applyRegion(regionSelect.value);
  }

  function run() {
    document.querySelectorAll('.size-chart-popup').forEach(initWrapper);
  }

  run();
  document.addEventListener('shopify:section:load', run);
})();
